import React, { useCallback, useEffect, useState } from "react";

import Modal, { ModalProps } from "../../../useOss/Modal";

import "../../../style/create-new-app-env.module.css";
import { CopyBox2 } from "../../../components/elements/CopyBox";
import InlineNotification from "../../../components/elements/Notification";
import { Spinner } from "../../../useOss";
import Storage from "../../../utils/storage";

import { updateMultiTenancyApp } from "../../../api/saas/multi-tenancy/connection-uri-domain/appid/put";
import { APP_ID_STORAGE_KEY } from "../index";
import { usePaymentsModal } from "../../../context/PaymentsModalContext";
import { usePaymentDetailsContext } from "../../../utils/payments";
import { PaymentsError } from "../../../utils/customErrors";
import saasAnalytics from "../saasAnalytics";
import { getStripeRedirectURLs } from "../../../utils";

type CreateNewAppOrEnvProps = {
    deploymentId: string;
    connectionHost: string;
    apiKey: string;
};

type CreateAppModalProps = ModalProps & CreateNewAppOrEnvProps;

const CreateAppModal = (props: CreateAppModalProps) => {
    const { show, modalClosed } = props;

    const { openModal: openPaymentsModal } = usePaymentsModal();
    const paymentDetailsContext = usePaymentDetailsContext();

    let defaultAppName: string = "";

    if (paymentDetailsContext !== undefined && paymentDetailsContext.savedContext.action_type === "create-app-or-env") {
        defaultAppName = paymentDetailsContext.savedContext.data.appName;
    }

    const [activeSection, setActiveSection] = useState<"important-info" | "create-new-app">(
        defaultAppName !== "" ? "create-new-app" : "important-info"
    );
    const [appName, setAppName] = useState(defaultAppName);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);

    async function handleCreate(e?: React.FormEvent<HTMLFormElement>) {
        if (e) e.preventDefault();
        setIsLoading(true);

        saasAnalytics.createNewAppOrEnvCreateNow();

        try {
            const reg = new RegExp(/^[a-z0-9]+/i);
            const lowercasedAppName = appName.toLowerCase();

            if (/\s/g.test(lowercasedAppName)) {
                setErrorMessage("App name should not contain any spaces.");
                return;
            }

            if (lowercasedAppName.startsWith("appid-")) {
                setErrorMessage("App name cannot start with appid-");
                return;
            }

            if (reg.test(lowercasedAppName) === false) {
                setErrorMessage("Please enter a valid name.");
                return;
            }
            const { cancelURL, successURL } = getStripeRedirectURLs();
            await updateMultiTenancyApp(lowercasedAppName, {
                config: [],
                deploymentId: props.deploymentId,
                deploymentType: "development",
                cancelURL,
                successURL
            });

            Storage.setItem(APP_ID_STORAGE_KEY, lowercasedAppName);
            window.location.reload();
        } catch (e) {
            if (PaymentsError.isThisError(e)) {
                modalClosed();
                saasAnalytics.paymentDetailsPopup("create_app_or_env");
                openPaymentsModal({
                    action_type: "create-app-or-env",
                    data: {
                        appName
                    },
                    redirect_uri: e.redirectionURL,
                    timestamp: Date.now()
                });
            } else {
                setErrorMessage("Something went wrong, Please try again.");
            }
        } finally {
            setIsLoading(false);
            setTimeout(() => {
                setErrorMessage(undefined);
            }, 5000);
        }
    }

    useEffect(() => {
        if (
            paymentDetailsContext !== undefined &&
            paymentDetailsContext.savedContext.action_type === "create-app-or-env" &&
            paymentDetailsContext.isPaymentSuccess === "true"
        ) {
            handleCreate();
        }
    }, [paymentDetailsContext]);

    const renderImportantInfo = useCallback(() => {
        return (
            <div className="modal-container">
                <div className="modal-header">
                    <h2>Important Info</h2>
                    <span
                        className="close-btn"
                        onClick={() => {
                            saasAnalytics.createNewAppOrEnvModalClose("important_info");
                            modalClosed();
                        }}
                    >
                        <img src="static/assets/dashboard/close-rounded.png" />
                    </span>
                </div>
                <div className="modal-body">
                    <div className="info-container">
                        <span className="info-pill">This is a paid feature</span>
                        <ul>
                            <li>
                                <span>Price: $50/month for each environment</span>
                                <br />
                                Each new app comprises of two envs (dev and prod), and by default, only the dev env will
                                be deployed for you. If you choose to also deploy a prod env for the app, the charge
                                will be $100/month for that app ($50 for dev, and $50 for prod).
                            </li>
                            <li>
                                This app can be used as an entirely different application or as a staging/testing
                                environment for your existing app.
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="modal-action-container">
                    <button
                        onClick={() => {
                            saasAnalytics.createNewAppOrEnvContinue();
                            setActiveSection("create-new-app");
                        }}
                        className="yellow-button app-btn"
                    >
                        Continue
                    </button>
                </div>
            </div>
        );
    }, []);

    const renderCreateNewApp = useCallback(() => {
        return (
            <div className="modal-container">
                <div className="modal-header">
                    <h2>Create New app</h2>
                    <span
                        className="close-btn"
                        onClick={() => {
                            saasAnalytics.createNewAppOrEnvModalClose("create_new_app");
                            modalClosed();
                        }}
                    >
                        <img src="static/assets/dashboard/close-rounded.png" />
                    </span>
                </div>
                <div className="modal-body">
                    <div className="modal-action-container">
                        <form className="new-app-form" onSubmit={handleCreate}>
                            <label htmlFor="appName">Name of new Env / App:</label>
                            <input
                                required
                                value={appName}
                                onChange={e => setAppName(e.currentTarget.value)}
                                type="text"
                                name="appName"
                                id="appName"
                                placeholder="Enter Name Here"
                            />
                            <div className="modal-action-container">
                                <a
                                    role="button"
                                    onClick={() => {
                                        saasAnalytics.createNewAppOrEnvGoBack();
                                        setActiveSection("important-info");
                                    }}
                                    className="white-button app-btn"
                                >
                                    Go Back
                                </a>
                                <button disabled={isLoading} type="submit" className="yellow-button app-btn">
                                    {isLoading ? <Spinner isInline /> : "Create Now"}
                                </button>
                            </div>
                        </form>
                    </div>
                    {errorMessage ? (
                        <div className="notification-holder">
                            <InlineNotification status="error">{errorMessage}</InlineNotification>
                        </div>
                    ) : null}
                    <div className="or-section">
                        <hr />
                        <span>or</span>
                    </div>
                    <span className="curl-heading">Use cURL command to create yourself:</span>
                    <CopyBox2
                        overrideClass="override-copy-box"
                        copyCallback={saasAnalytics.createNewAppOrEnvCommandCopy}
                        value={`curl --location --request PUT '${props.connectionHost}/recipe/multitenancy/app' \
                        --header 'api-key: ${props.apiKey}' \
                        --header 'Content-Type: application/json' \
                        --data '{
                            "appId": "${appName ? appName.toLowerCase().replace(/ /g, "") : "<APP_NAME>"}"
                        }'`}
                    />
                </div>
            </div>
        );
    }, [appName, errorMessage, isLoading]);

    return (
        <React.Fragment>
            <Modal disableBackdropClose={true} show={show} modalClosed={modalClosed}>
                {activeSection === "important-info" ? renderImportantInfo() : renderCreateNewApp()}
            </Modal>
        </React.Fragment>
    );
};

export default function CreateNewAppOrEnv(props: CreateNewAppOrEnvProps) {
    const paymentDetailsContext = usePaymentDetailsContext();
    let defaultValue = false;

    if (paymentDetailsContext !== undefined && paymentDetailsContext.savedContext.action_type === "create-app-or-env") {
        defaultValue = true;
    }

    const [showAppCreationModal, setShowAppCreationModal] = useState(defaultValue);

    return (
        <React.Fragment>
            {/* <button
                onClick={() => {
                    setShowAppCreationModal(true);
                    saasAnalytics.createNewAppOrEnv();
                }}
                className="btn btn-sm create-app-btn app-btn"
            >
                Create New Env / App
            </button> */}
            {showAppCreationModal ? (
                <CreateAppModal
                    {...props}
                    modalClosed={() => setShowAppCreationModal(false)}
                    show={showAppCreationModal}
                />
            ) : null}
        </React.Fragment>
    );
}
